import type { RunStatus } from '@/components/patterns/status-badge';

/**
 * Les types de job du registre (26 + le job SQL du lot L05) — source unique : `docs/porting/registre-types-de-job.md` §0
 * (décision du 27/09 : **tous les types sont à terminer**, aucun n'est retiré par choix de produit).
 * L'ordre est celui de la roue de sélection : les quatre historiques d'abord.
 *
 * Ce fichier n'importe rien de l'API ni des patterns : il peut donc être lu des deux côtés
 * (`src/api/contract.ts`, les mocks, le registre des descripteurs).
 */
export const JOB_TYPES = [
  'python',
  'bash',
  'spark',
  'lowcode',
  'docker',
  'dbt',
  'dask',
  'ray',
  'flink',
  'tensorflow',
  'pytorch',
  'mxnet',
  'xgboost',
  'knime',
  'database-migration',
  'hive',
  'apachesqoop',
  'apachehadoop',
  'apachemapreduce',
  'beam',
  'spring-batch',
  'spring-cloud-data-flow',
  'akka-data-pipeline',
  'pulsar',
  'airflow',
  'sql',
  'sas',
  // Lot L02 (ADR 0042 §4) : un .ipynb exécuté de bout en bout (nbclient), sorties dans MLflow.
  'notebook',
] as const;

export type JobType = (typeof JOB_TYPES)[number];

export const isJobType = (value: unknown): value is JobType =>
  typeof value === 'string' && (JOB_TYPES as readonly string[]).includes(value);

/**
 * Types **prouvés** de bout en bout (registre §0, colonne « Prouvé au 27/09 » : run `complete`
 * sur k3d). Les autres restent sélectionnables, avec le badge « bientôt » : l'API répond
 * `501 feature_disabled` à leur création tant que leur preuve n'est pas faite (revue, C10).
 * Passer un type ici = une preuve de plus au registre, pas un choix de console.
 */
export const PROVEN_JOB_TYPES: readonly JobType[] = [
  'python',
  'bash',
  // Lot L05 (2026-10-11) : runs réels `complete` sur k3d, dev/smoke/runtimes-ml.sh et dev/smoke/job-sql.sh.
  'tensorflow',
  'pytorch',
  'mxnet',
  'xgboost',
  'knime',
  'airflow',
  'sql',
  // 2026-10-23, sur dev : `sas` (projet énergie multi-fichiers, run complete, 62 étapes, 0 ERROR — ADR 0052) et
  // `apachemapreduce` (hadoop-demo.sh --scenario regions, complete ; WordCount réparti par le pont YARN) ;
  // `apachesqoop` : import par connexion de données puis MapReduce s3a, run réel complete sur k3d (#180).
  'sas',
  'apachemapreduce',
  'apachesqoop',
];

export type Owner =
  | { kind: 'user'; name: string; initials: string }
  | { kind: 'agent'; name: string; client: string; onBehalfOf: string };

export type Job = {
  id: string;
  name: string;
  type: JobType;
  status: RunStatus;
  lastRunNumber: number;
  lastRunAt: string;
  durationSec: number | null;
  schedule: string | null;
  owner: Owner;
};

export type Run = {
  id: string;
  number: number;
  jobName: string;
  project: string;
  status: RunStatus;
  startedAt: string;
  elapsedSec: number;
  image: string;
  resources: string;
  instanceType: string;
  attempt: string;
  schedule: string | null;
  triggeredBy: Owner;
};
